import React from 'react';
import { 
  Box, 
  Typography, 
  LinearProgress, 
  Button, 
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import { ClaimStage } from '../../../interfaces/billing';
import { isActiveStatus } from './ProcessStatusIndicator';
import styles from './ProcessProgressMessage.module.css';


interface ProcessProgressMessageProps {
  status: ClaimStage;
  processId: number;
  message?: string;
  progress?: number;
  estimatedTimeRemaining?: string;
  startedAt?: string;
  lastUpdated?: string;
  onRefresh?: () => void;
  onCancel?: () => void;
  showDetails?: boolean;
  className?: string;
}

/**
 * Component for displaying detailed progress messages and controls
 */
export const ProcessProgressMessage: React.FC<ProcessProgressMessageProps> = ({
  status,
  processId,
  message,
  progress,
  estimatedTimeRemaining,
  startedAt,
  lastUpdated,
  onRefresh,
  onCancel,
  showDetails = false,
  className,
}) => {
  const [detailsExpanded, setDetailsExpanded] = React.useState(showDetails);

  const getStatusConfig = () => {
    switch (status) {
      case ClaimStage.CreationInProgress:
        return {
          severity: 'info' as const,
          title: 'Creating Claims',
          defaultMessage: 'Your claims are being created in the background. This may take a few minutes.',
          showProgress: true,
          showCancel: true,
        };
      case ClaimStage.Created:
        return {
          severity: 'success' as const,
          title: 'Claims Created Successfully',
          defaultMessage: 'All claims have been created successfully. You can now proceed to export them.',
          showProgress: false,
          showCancel: false,
        };
      case ClaimStage.ExportInProgress:
        return {
          severity: 'info' as const,
          title: 'Exporting Claims',
          defaultMessage: 'Your claims are being exported to external systems. This may take a few minutes.',
          showProgress: true,
          showCancel: false,
        };
      case ClaimStage.Exported:
        return {
          severity: 'success' as const,
          title: 'Claims Exported Successfully',
          defaultMessage: 'All claims have been exported successfully. You can now download reports or manage notes.',
          showProgress: false,
          showCancel: false,
        };
      case ClaimStage.Discarded:
        return {
          severity: 'error' as const,
          title: 'Process Cancelled',
          defaultMessage: 'The process was cancelled or encountered an error. Please try again.',
          showProgress: false,
          showCancel: false,
        };
      default:
        return {
          severity: 'warning' as const,
          title: 'Unknown Status',
          defaultMessage: 'Process status is unknown. Please refresh to get the latest information.',
          showProgress: false,
          showCancel: false,
        };
    }
  };

  const statusConfig = getStatusConfig();
  const displayMessage = message || statusConfig.defaultMessage;

  const formatDuration = (startTime: string): string => {
    try {
      const start = new Date(startTime);
      const now = new Date();
      const durationMs = now.getTime() - start.getTime();
      const minutes = Math.floor(durationMs / (1000 * 60));
      const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
      
      if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      }
      return `${seconds}s`;
    } catch (error) {
      return '';
    }
  };

  const getProgressValue = (): number | undefined => {
    if (progress !== undefined) {
      return progress;
    }
    
    // Default progress based on status
    switch (status) {
      case ClaimStage.CreationInProgress:
      case ClaimStage.ExportInProgress:
        return undefined; // Indeterminate
      case ClaimStage.Created:
        return 50; // Halfway through the process
      case ClaimStage.Exported:
        return 100; // Complete
      case ClaimStage.Discarded:
        return 0; // Failed
      default:
        return 0;
    }
  };

  const progressValue = getProgressValue();

  return (
    <Box className={`${styles.progressContainer} ${className || ''}`}>
      <Alert 
        severity={statusConfig.severity}
        icon={<InfoIcon />}
        action={
          <Box display="flex" alignItems="center" gap={1}>
            {onRefresh && (
              <IconButton
                size="small"
                onClick={onRefresh}
                aria-label="Refresh status"
              >
                <RefreshIcon />
              </IconButton>
            )}
            {(startedAt || lastUpdated) && (
              <IconButton
                size="small"
                onClick={() => setDetailsExpanded(!detailsExpanded)}
                aria-label="Toggle details"
              >
                {detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
        }
      >
        <AlertTitle>{statusConfig.title}</AlertTitle>
        <Typography variant="body2">
          {displayMessage}
        </Typography>
        
        {statusConfig.showProgress && (
          <Box mt={1}>
            <LinearProgress
              variant={progressValue !== undefined ? 'determinate' : 'indeterminate'}
              value={progressValue}
              sx={{ borderRadius: 1, height: 6 }}
            />
            {estimatedTimeRemaining && (
              <Typography variant="caption" className={styles.estimatedTime}>
                Estimated time remaining: {estimatedTimeRemaining}
              </Typography>
            )}
          </Box>
        )}
        
        <Collapse in={detailsExpanded}>
          <Box className={styles.progressContent}>
            <Typography variant="body2" color="text.secondary">
              <strong>Process ID:</strong> {processId}
            </Typography>
            {startedAt && (
              <Typography variant="body2" color="text.secondary">
                <strong>Started:</strong> {new Date(startedAt).toLocaleString()}
                {' '}({formatDuration(startedAt)} ago)
              </Typography>
            )}
            {lastUpdated && (
              <Typography variant="body2" color="text.secondary">
                <strong>Last Updated:</strong> {new Date(lastUpdated).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Collapse>
        
        {(statusConfig.showCancel || statusConfig.severity === 'error') && (
          <Box className={styles.actionButtons}>
            {statusConfig.showCancel && onCancel && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={onCancel}
              >
                Cancel Process
              </Button>
            )}
            {statusConfig.severity === 'error' && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={onRefresh}
              >
                Retry
              </Button>
            )}
          </Box>
        )}
      </Alert>
    </Box>
  );
};

/**
 * Compact version of progress message for inline display
 */
export const CompactProgressMessage: React.FC<{
  status: ClaimStage;
  message?: string;
  showIcon?: boolean;
}> = ({ status, message, showIcon = true }) => {
  const statusConfig = (() => {
    switch (status) {
      case ClaimStage.CreationInProgress:
        return { color: 'primary', defaultMessage: 'Creating...' };
      case ClaimStage.Created:
        return { color: 'success', defaultMessage: 'Created' };
      case ClaimStage.ExportInProgress:
        return { color: 'secondary', defaultMessage: 'Exporting...' };
      case ClaimStage.Exported:
        return { color: 'success', defaultMessage: 'Exported' };
      case ClaimStage.Discarded:
        return { color: 'error', defaultMessage: 'Cancelled' };
      default:
        return { color: 'text.secondary', defaultMessage: 'Unknown' };
    }
  })();

  return (
    <Typography
      variant="body2"
      color={statusConfig.color}
      className={styles.compactMessage}
    >
      {showIcon && isActiveStatus(status) && (
        <Box className={styles.pulseIcon} />
      )}
      {message || statusConfig.defaultMessage}
    </Typography>
  );
};

export default ProcessProgressMessage;